import React, { useState } from 'react';
import {
  Form,
  Input,
  Button,
  Space,
  Card,
  Upload,
  message,
  Radio,
  Switch,
  Typography,
  Image,
  Modal
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  UploadOutlined,
  LinkOutlined
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import api, { getStaticImageUrl } from '../services/api';
import { QuestionType, TestDifficulty } from '../types';
import { TEST_DIFFICULTY_OPTIONS } from '../constants/testDifficulty';
import { getApiErrorMessage } from '../utils/apiError';

// Убеждаемся, что QuestionType доступен
const QuestionTypeEnum = QuestionType;

const { TextArea } = Input;
const { Text } = Typography;

interface Answer {
  id?: number;
  text: string;
  is_correct: boolean;
  order: number;
  matching_key?: string;
  image_url?: string;
}

interface QuestionFormData {
  text: string;
  type: QuestionType;
  order: number;
  difficulty?: TestDifficulty | null;
  points: number;
  image_url?: string;
  explanation?: string;
  explanation_image_urls?: string[];
  explanation_image_captions?: string[];
  propagate_images_to_duplicates?: boolean;
  update_scope?: 'single' | 'duplicates';
  confirmation_token?: string;
  answers: Answer[];
}

interface QuestionFormProps {
  testId: number;
  onSuccess?: () => void;
  onCancel?: () => void;
  initialData?: any;
}

const QuestionForm: React.FC<QuestionFormProps> = ({
  testId,
  onSuccess,
  onCancel,
  initialData
}) => {
  const [form] = Form.useForm();
  const [questionType, setQuestionType] = useState<'closed' | 'open'>('closed');
  const [multipleChoice, setMultipleChoice] = useState(false);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [imageUploadMethod, setImageUploadMethod] = useState<'url' | 'file'>('url');
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');

  // Инициализация формы
  React.useEffect(() => {
    if (initialData) {
      const questionTypeStr = initialData.type;
      const isOpen = questionTypeStr === QuestionTypeEnum.OPEN_ANSWER || questionTypeStr === 'open_answer';
      const isMultiple = questionTypeStr === QuestionTypeEnum.MULTIPLE_CHOICE || questionTypeStr === 'multiple_choice';
      
      // Устанавливаем тип вопроса
      if (isOpen) {
        setQuestionType('open');
      } else {
        setQuestionType('closed');
        setMultipleChoice(isMultiple);
      }
      
      // Подготавливаем варианты ответов
      let answersData = [];
      if (!isOpen && initialData.answers && initialData.answers.length > 0) {
        answersData = initialData.answers.map((answer: any) => ({
          id: answer.id,
          text: answer.text || '',
          is_correct: answer.is_correct || false,
          order: answer.order || 0,
          matching_key: answer.matching_key || undefined,
          image_url: answer.image_url || undefined
        }));
      } else if (!isOpen) {
        // Если нет вариантов, добавляем один пустой
        answersData = [{ text: '', is_correct: false, image_url: undefined }];
      }
      
      // Устанавливаем значения формы
      const urls = initialData.explanation_image_urls || [];
      const captions = initialData.explanation_image_captions || [];
      const explanationImageLines = Array.isArray(urls)
        ? urls.map((u: string, i: number) => (captions[i] ? `${u} | ${captions[i]}` : u))
        : [];
      form.setFieldsValue({
        text: initialData.text || '',
        order: initialData.order || 1,
        difficulty: initialData.difficulty || '',
        points: initialData.points || 1,
        image_url: initialData.image_url || '',
        explanation: initialData.explanation || '',
        explanation_image_urls: explanationImageLines.join('\n'),
        propagate_images_to_duplicates: false,
        answers: answersData
      });
      
      // Устанавливаем изображение
      if (initialData.image_url) {
        setImageUrl(initialData.image_url);
        setPreviewImage(initialData.image_url);
      } else {
        setImageUrl('');
        setPreviewImage('');
      }
    } else {
      // Сброс формы при создании нового вопроса
      form.resetFields();
      setQuestionType('closed');
      setMultipleChoice(false);
      setImageUrl('');
      setPreviewImage('');
      form.setFieldsValue({
        order: 1,
        points: 1,
        propagate_images_to_duplicates: false,
        answers: [{ text: '', is_correct: false }]
      });
    }
  }, [initialData, form]);

  // Обработка загрузки изображения по файлу
  const handleImageUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file as File);
      
      const response = await api.post('/images/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const uploadedUrl = response.data.url;
      setImageUrl(uploadedUrl);
      setPreviewImage(uploadedUrl);
      form.setFieldsValue({ image_url: uploadedUrl });
      
      message.success('Изображение успешно загружено');
      onSuccess?.(response.data);
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Ошибка загрузки изображения'));
      onError?.(error);
    } finally {
      setUploading(false);
    }
  };

  // Обработка изменения URL изображения
  const handleImageUrlChange = (url: string) => {
    setImageUrl(url);
    setPreviewImage(url);
    form.setFieldsValue({ image_url: url });
  };

  // Обработка отправки формы
  const handleSubmit = async (values: any) => {
    try {
      // Определяем тип вопроса
      const questionTypeValue: QuestionType = questionType === 'open'
        ? QuestionTypeEnum.OPEN_ANSWER
        : (multipleChoice 
          ? QuestionTypeEnum.MULTIPLE_CHOICE 
          : QuestionTypeEnum.SINGLE_CHOICE);

      const explanationLinesRaw = values.explanation_image_urls || '';
      const explanation_image_urls: string[] = [];
      const explanation_image_captions: string[] = [];
      explanationLinesRaw.split(/\r?\n/).forEach((line: string) => {
        const t = line.trim();
        if (!t) return;
        const pipe = t.indexOf('|');
        if (pipe >= 0) {
          explanation_image_urls.push(t.slice(0, pipe).trim());
          explanation_image_captions.push(t.slice(pipe + 1).trim());
        } else {
          explanation_image_urls.push(t);
          explanation_image_captions.push('');
        }
      });

      const questionData: QuestionFormData = {
        text: values.text,
        type: questionTypeValue as QuestionType,
        order: values.order || 1,
        difficulty: values.difficulty || null,
        points: values.points || 1,
        image_url: imageUrl || undefined,
        explanation: values.explanation?.trim() || undefined,
        explanation_image_urls: explanation_image_urls.length > 0 ? explanation_image_urls : undefined,
        explanation_image_captions: explanation_image_urls.length > 0 ? explanation_image_captions : undefined,
        propagate_images_to_duplicates: Boolean(values.propagate_images_to_duplicates),
        answers: []
      };

      // Для закрытых вопросов добавляем варианты ответов
      if (questionType === 'closed') {
        const answers = form.getFieldValue('answers') || [];
        console.log('Saving answers with data:', answers); // Отладочное логирование
        const existingOrders = answers
          .filter((answer: any) => answer.id != null && Number.isFinite(Number(answer.order)))
          .map((answer: any) => Number(answer.order));
        const usedOrders = new Set<number>(existingOrders);
        let nextOrder = existingOrders.length > 0 ? Math.max(...existingOrders) + 1 : 0;
        questionData.answers = answers.map((answer: any, index: number) => {
          // Явно проверяем наличие image_url и не пустую строку
          const imageUrl = answer.image_url && answer.image_url.trim() !== '' 
            ? answer.image_url.trim() 
            : undefined;
          let answerOrder = Number(answer.order);
          if (answer.id == null || !Number.isFinite(answerOrder)) {
            while (usedOrders.has(nextOrder)) nextOrder += 1;
            answerOrder = nextOrder;
            usedOrders.add(answerOrder);
            nextOrder += 1;
          }
          const answerData = {
            id: answer.id,
            text: answer.text || '',
            is_correct: answer.is_correct || false,
            order: answerOrder,
            matching_key: answer.matching_key || undefined,
            image_url: imageUrl
          };
          console.log(`Answer ${index}:`, answerData); // Отладочное логирование
          return answerData;
        });
      }

      const finishSaving = () => {
        form.resetFields();
        setImageUrl('');
        setPreviewImage('');
        onSuccess?.();
      };

      const saveUpdatedQuestion = async (
        updateScope?: 'single' | 'duplicates',
        confirmationToken?: string
      ) => {
        const payload: QuestionFormData = updateScope
          ? {
              ...questionData,
              update_scope: updateScope,
              confirmation_token: confirmationToken
            }
          : questionData;
        return api.put(`/admin/questions/${initialData.id}`, payload);
      };

      const showUpdateSuccess = (
        response: any,
        updateScope?: 'single' | 'duplicates'
      ) => {
        const propagated = response.data?.propagated_questions || 0;
        if (updateScope === 'duplicates') {
          message.success(
            `Вопрос и ответы обновлены во всех тестах: ещё ${propagated} копий`
          );
        } else if (updateScope === 'single') {
          message.success(
            values.propagate_images_to_duplicates
              ? `Вопрос обновлён только в этом тесте, фото — ещё в ${propagated} копиях`
              : 'Вопрос обновлён только в текущем тесте'
          );
        } else {
          message.success(
            values.propagate_images_to_duplicates
              ? `Вопрос обновлён, фото применены ещё к ${propagated} копиям вопроса`
              : 'Вопрос успешно обновлён'
          );
        }
        finishSaving();
      };

      // Отправляем запрос
      if (initialData) {
        try {
          // Первый PUT без scope: backend запросит выбор только при наличии копий.
          const response = await saveUpdatedQuestion();
          showUpdateSuccess(response);
        } catch (error: any) {
          const detail = error?.response?.data?.detail;
          if (
            error?.response?.status === 409
            && detail?.code === 'duplicate_question_update_confirmation_required'
          ) {
            const duplicateQuestions = Number(detail.duplicate_questions_count) || 0;
            const duplicateTests = Number(detail.duplicate_tests_count) || duplicateQuestions;
            const excludedConflicts = Number(
              detail.excluded_conflicting_questions_count
            ) || 0;
            const confirmationToken = String(detail.confirmation_token || '');
            const conflictWarning = excludedConflicts > 0
              ? ` Ещё ${excludedConflicts} вопросов имеют такую же формулировку, но другое содержимое; они не будут изменены.`
              : '';
            const handleConfirmationSaveError = (
              saveError: any,
              fallback: string
            ) => {
              message.error(getApiErrorMessage(saveError, fallback));
              if (
                saveError?.response?.status === 409
                && saveError?.response?.data?.detail?.code === 'duplicate_question_update_stale'
              ) {
                // The data shown in this editor is stale. Close both modals and
                // reload the question list so the next edit starts from fresh data.
                Modal.destroyAll();
                onSuccess?.();
                return true;
              }
              return false;
            };

            Modal.confirm({
              title: 'Где обновить вопрос?',
              content: `Найдено ещё ${duplicateQuestions} одинаковых копий в ${duplicateTests} тестах. Можно изменить только текущий тест или синхронизировать текст, ответы, пояснения и фото во всех копиях.${conflictWarning}`,
              okText: 'Во всех тестах',
              cancelText: 'Только в этом тесте',
              closable: false,
              keyboard: false,
              maskClosable: false,
              onOk: async () => {
                try {
                  const response = await saveUpdatedQuestion(
                    'duplicates',
                    confirmationToken
                  );
                  showUpdateSuccess(response, 'duplicates');
                } catch (saveError: any) {
                  if (!handleConfirmationSaveError(
                    saveError,
                    'Ошибка при массовом обновлении'
                  )) {
                    throw saveError;
                  }
                }
              },
              onCancel: async () => {
                try {
                  const response = await saveUpdatedQuestion(
                    'single',
                    confirmationToken
                  );
                  showUpdateSuccess(response, 'single');
                } catch (saveError: any) {
                  if (!handleConfirmationSaveError(
                    saveError,
                    'Ошибка при сохранении вопроса'
                  )) {
                    throw saveError;
                  }
                }
              }
            });
            return;
          }
          throw error;
        }
      } else {
        // Создание нового вопроса
        await api.post(`/admin/tests/${testId}/questions`, questionData);
        message.success('Вопрос успешно добавлен');
        finishSaving();
      }
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Ошибка при сохранении вопроса'));
    }
  };

  return (
    <Card
      title={initialData ? 'Редактирование вопроса' : 'Добавление нового вопроса'}
      style={{ marginBottom: 24 }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          order: 1,
          points: 1,
          propagate_images_to_duplicates: false,
          answers: [{ text: '', is_correct: false }]
        }}
      >
        {/* Тип вопроса */}
        <Form.Item label="Тип вопроса" required>
          <Radio.Group
            value={questionType}
            onChange={(e) => {
              setQuestionType(e.target.value);
              if (e.target.value === 'open') {
                form.setFieldsValue({ answers: [] });
              } else {
                form.setFieldsValue({ answers: [{ text: '', is_correct: false }] });
              }
            }}
          >
            <Radio value="closed">Закрытый (с вариантами ответов)</Radio>
            <Radio value="open">Открытый (текстовый ответ)</Radio>
          </Radio.Group>
        </Form.Item>

        {/* Множественный выбор (только для закрытых вопросов) */}
        {questionType === 'closed' && (
          <Form.Item label="Тип выбора">
            <Switch
              checked={multipleChoice}
              onChange={setMultipleChoice}
              checkedChildren="Множественный выбор"
              unCheckedChildren="Один ответ"
            />
            <Text type="secondary" style={{ marginLeft: 8 }}>
              {multipleChoice ? 'Можно выбрать несколько правильных ответов' : 'Только один правильный ответ'}
            </Text>
          </Form.Item>
        )}

        {/* Текст вопроса */}
        <Form.Item
          name="text"
          label="Текст вопроса"
          rules={[{ required: true, message: 'Введите текст вопроса' }]}
        >
          <TextArea
            rows={4}
            placeholder="Введите текст вопроса..."
          />
        </Form.Item>

        {/* Изображение к вопросу */}
        <Form.Item label="Изображение к вопросу">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Radio.Group
              value={imageUploadMethod}
              onChange={(e) => setImageUploadMethod(e.target.value)}
              style={{ marginBottom: 16 }}
            >
              <Radio value="url">
                <LinkOutlined /> По ссылке
              </Radio>
              <Radio value="file">
                <UploadOutlined /> Загрузить файл
              </Radio>
            </Radio.Group>

            {imageUploadMethod === 'url' ? (
              <Input
                placeholder="Введите URL изображения"
                value={imageUrl}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                prefix={<LinkOutlined />}
                style={{ marginBottom: 8 }}
              />
            ) : (
              <Upload
                customRequest={handleImageUpload}
                showUploadList={false}
                accept="image/*"
                maxCount={1}
              >
                <Button icon={<UploadOutlined />} loading={uploading}>
                  {uploading ? 'Загрузка...' : 'Выбрать файл'}
                </Button>
              </Upload>
            )}
            {previewImage && (
              <div style={{ marginTop: 16 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                  Предпросмотр:
                </Text>
                <Image
                  src={getStaticImageUrl(previewImage)}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3a0DVQHBH2A1C3R2YqLyBxW8/2/3z3zrPn8d9fz5fN0nFkMAr9AcyUsag6p4QAEuJJSBMopYWg4zi3gfiJS4sLmKICtm4l6BqA+BFZHsbhCj+EPYZMcyCHos8DKWQMMabJQ60gf81FKR0hPIp0HYEwCxZfIFLSUC2VXIDC8vo6WoFGhXeTr9vaGRgYExEyjpCNFpUGBYZaTAwSDusMDiIgQyxJmxs8gYHDjxhBw8zQxMCxgeXGBJxgODmSxkYGLYx6BYlFiXAHMH38jkuKQYgpCZm4MT6TxfyEsCswqzBwmCTGEhFmbNxYBTwuGTI4FeCzfZG9jtgYGCxT7T//8+czoLA8k8Hv+///73o//+/i4HmX4OA98JAOX5fW0SxsawAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                />
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setImageUrl('');
                    setPreviewImage('');
                    form.setFieldsValue({ image_url: undefined });
                  }}
                  style={{ marginTop: 8 }}
                >
                  Удалить изображение
                </Button>
              </div>
            )}
          </Space>
        </Form.Item>

        {initialData && (
          <Form.Item
            name="propagate_images_to_duplicates"
            label="Массовое обновление фотографий"
            valuePropName="checked"
            extra="При сохранении фото вопроса, фото пояснения и фото вариантов ответа будут скопированы во все вопросы с таким же текстом во всех тестах. Тексты и правильные ответы не изменятся."
          >
            <Switch
              checkedChildren="Во все копии"
              unCheckedChildren="Только этот вопрос"
            />
          </Form.Item>
        )}

        {/* Пояснение после неверного ответа (показывается в конце теста и после ошибки) */}
        <Form.Item
          name="explanation"
          label="Пояснение к ответу"
        >
          <TextArea
            rows={6}
            placeholder="Текст пояснения, которое увидит проходящий тест после неверного ответа и в конце теста. Можно описать правильный ответ и вариации (например, артикулы FEBEST)."
          />
        </Form.Item>
        <Form.Item
          name="explanation_image_urls"
          label="Фото для пояснения (артикул — через | после URL)"
        >
          <TextArea
            rows={3}
            placeholder="Одна строка — одно фото. Чтобы подписать артикулом: URL | HAB-110F. Пример: /static/images/photo.jpg | HAB-110F"
          />
        </Form.Item>

        {/* Варианты ответов (только для закрытых вопросов) */}
        {questionType === 'closed' && (
          <Form.Item label="Варианты ответов">
            <Form.List name="answers">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => {
                    const answerImageUrl = form.getFieldValue(['answers', name, 'image_url']);
                    return (
                      <div key={key} style={{ marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 8 }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="small">
                          <Form.Item {...restField} name={[name, 'id']} hidden>
                            <Input />
                          </Form.Item>
                          <Form.Item {...restField} name={[name, 'matching_key']} hidden>
                            <Input />
                          </Form.Item>
                          <Space style={{ display: 'flex', width: '100%' }} align="baseline">
                            <Form.Item
                              {...restField}
                              name={[name, 'text']}
                              rules={[{ required: true, message: 'Введите текст ответа' }]}
                              style={{ flex: 1, marginBottom: 0 }}
                            >
                              <Input placeholder="Текст варианта ответа" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, 'is_correct']}
                              valuePropName="checked"
                              style={{ marginBottom: 0 }}
                            >
                              <Switch
                                checkedChildren="✓"
                                unCheckedChildren="✗"
                              />
                            </Form.Item>
                            {fields.length > 1 && (
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(name)}
                              />
                            )}
                          </Space>
                          
                          {/* Изображение для варианта ответа */}
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>
                              Изображение (опционально):
                            </Text>
                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                              <Form.Item
                                {...restField}
                                name={[name, 'image_url']}
                                style={{ marginBottom: 0 }}
                              >
                                <Input
                                  placeholder="URL изображения или загрузите файл"
                                  prefix={<LinkOutlined />}
                                  style={{ width: '100%' }}
                                  onPaste={(e) => {
                                    // Обработка вставки из буфера обмена
                                    const pastedText = e.clipboardData.getData('text');
                                    if (pastedText && (pastedText.startsWith('http') || pastedText.startsWith('/'))) {
                                      setTimeout(() => {
                                        form.setFieldsValue({
                                          answers: form.getFieldValue('answers').map((ans: any, idx: number) => 
                                            idx === name ? { ...ans, image_url: pastedText } : ans
                                          )
                                        });
                                      }, 0);
                                    }
                                  }}
                                />
                              </Form.Item>
                              <Upload
                                customRequest={async (options) => {
                                  const { file, onSuccess, onError } = options;
                                  try {
                                    const formData = new FormData();
                                    formData.append('file', file as File);
                                    const response = await api.post('/images/upload', formData, {
                                      headers: { 'Content-Type': 'multipart/form-data' },
                                    });
                                    const uploadedUrl = response.data.url;
                                    const currentAnswers = form.getFieldValue('answers') || [];
                                    currentAnswers[name] = { ...currentAnswers[name], image_url: uploadedUrl };
                                    form.setFieldsValue({ answers: currentAnswers });
                                    message.success('Изображение загружено');
                                    onSuccess?.(response.data);
                                  } catch (error: any) {
                                    message.error('Ошибка загрузки изображения');
                                    onError?.(error);
                                  }
                                }}
                                showUploadList={false}
                                accept="image/*"
                                maxCount={1}
                              >
                                <Button icon={<UploadOutlined />} size="small">
                                  Загрузить файл
                                </Button>
                              </Upload>
                              {answerImageUrl && (
                                <div style={{ marginTop: 8 }}>
                                  <Image
                                    src={getStaticImageUrl(answerImageUrl)}
                                    alt="Answer preview"
                                    width={100}
                                    height={100}
                                    style={{ objectFit: 'cover', borderRadius: 4 }}
                                    preview
                                  />
                                  <Button
                                    type="link"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => {
                                      const currentAnswers = form.getFieldValue('answers') || [];
                                      currentAnswers[name] = { ...currentAnswers[name], image_url: undefined };
                                      form.setFieldsValue({ answers: currentAnswers });
                                    }}
                                    style={{ marginLeft: 8 }}
                                  >
                                    Удалить
                                  </Button>
                                </div>
                              )}
                            </Space>
                          </div>
                        </Space>
                      </div>
                    );
                  })}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add({ text: '', is_correct: false })}
                      block
                      icon={<PlusOutlined />}
                    >
                      Добавить вариант ответа
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Form.Item>
        )}

        {/* Порядок и баллы */}
        <Space>
          <Form.Item
            name="order"
            label="Порядок"
            rules={[{ required: true, message: 'Укажите порядок' }]}
            style={{ width: 150 }}
          >
            <Input type="number" min={1} />
          </Form.Item>
          <Form.Item
            name="difficulty"
            label="Сложность вопроса"
          >
            <select className="tw-field">
              <option value="">Не задана</option>
              {TEST_DIFFICULTY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </Form.Item>
          <Form.Item
            name="points"
            label="Баллы"
            rules={[{ required: true, message: 'Укажите баллы' }]}
            style={{ width: 150 }}
          >
            <Input type="number" min={1} />
          </Form.Item>
        </Space>

        {/* Кнопки */}
        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit">
              {initialData ? 'Сохранить изменения' : 'Добавить вопрос'}
            </Button>
            {onCancel && (
              <Button onClick={onCancel}>
                Отмена
              </Button>
            )}
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
};

export default QuestionForm;
